import { BellOutlined } from "@ant-design/icons";
import { Button, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "shared/lib/antd-global";
import { usePushPermission } from "../hooks/use-push-permission";
import {
  dismissPushPrompt,
  isPushPromptDismissed,
} from "../lib/prompt-storage";
import { enablePush, isPushAvailable } from "../lib/push";
import styles from "./index.module.scss";

/**
 * Предложение включить уведомления (в браузере или в приложении).
 * dismissible — можно скрыть кнопкой «Не сейчас» (на главной);
 * в профиле блок показывается всегда, пока разрешение не выдано,
 * а при запрете — подсказка включить уведомления в настройках устройства.
 */
export const PushNotificationsBlock = ({
  dismissible,
}: {
  dismissible?: boolean;
}) => {
  const { t } = useTranslation();
  const [permission, setPermission] = usePushPermission();
  const [dismissed, setDismissed] = useState(
    () => !!dismissible && isPushPromptDismissed(),
  );
  const [isLoading, setIsLoading] = useState(false);

  const enable = async () => {
    setIsLoading(true);
    try {
      const result = await enablePush();
      setPermission(result);
      if (result === "granted") {
        message.success(t("push.enabled"));
      }
    } catch (error) {
      console.error("[push] enable failed", error);
      message.error(t("push.error"));
    } finally {
      setIsLoading(false);
    }
  };

  const dismiss = () => {
    dismissPushPrompt();
    setDismissed(true);
  };

  if (!isPushAvailable()) {
    return null;
  }

  if (permission === "denied" && !dismissible) {
    return (
      <div className={styles.block}>
        <div className={styles.iconWrapper}>
          <BellOutlined />
        </div>
        <Space vertical size="middle">
          {t("push.denied_title")}
          <div className={styles.text}>{t("push.denied_description")}</div>
        </Space>
      </div>
    );
  }

  if (permission !== "default" || dismissed) {
    return null;
  }

  return (
    <div className={styles.block}>
      <div className={styles.iconWrapper}>
        <BellOutlined />
      </div>
      <Space vertical size="middle">
        {t("push.title")}
        <div className={styles.text}>{t("push.description")}</div>
        <Space>
          <Button type="primary" onClick={enable} loading={isLoading}>
            {t("push.enable")}
          </Button>
          {dismissible && <Button onClick={dismiss}>{t("push.later")}</Button>}
        </Space>
      </Space>
    </div>
  );
};
